import React from "react";
import { Link } from "react-router-dom";
import { useSnapshot } from "@/lib/AppContext";
import { ReviewChip } from "@/components/chips";
import * as Icons from "lucide-react";

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl px-5 py-4" data-testid={testid}>
      <div className="text-3xl font-display text-stone-900">{value}</div>
      <div className="text-xs uppercase tracking-wider text-stone-500 mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { snapshot } = useSnapshot();
  if (!snapshot) return null;

  const counts = snapshot.counts || {};
  const totalAssets = snapshot.total_assets ?? Object.values(counts).reduce((a, b) => a + b, 0);
  const rs = snapshot.review_summary || {};
  const enr = snapshot.enrichment || {};

  return (
    <div className="p-8 max-w-6xl" data-testid="dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl text-stone-900">{snapshot.name}</h1>
          {snapshot.description && <p className="text-stone-600 mt-1">{snapshot.description}</p>}
          <p className="text-stone-500 mt-1 text-sm">
            Captured {new Date(snapshot.created_at).toLocaleString()} · Status:{" "}
            <span className={snapshot.status === "complete" ? "text-emerald-700" : "text-amber-700"}>{snapshot.status}</span>{" "}
            · {totalAssets} configuration assets
            {snapshot.reviews_carried_forward ? ` · ${snapshot.reviews_carried_forward} reviews carried forward` : ""}
          </p>
        </div>
        <Link to="/" data-testid="back-to-snapshots" className="inline-flex items-center gap-2 border border-stone-300 px-4 py-2 rounded-lg text-sm hover:bg-stone-100">
          <Icons.Layers className="w-4 h-4" /> All snapshots
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <Stat label="Total assets" value={totalAssets} testid="stat-total" />
        <Stat label="Keep" value={rs["Keep"] || 0} testid="stat-keep" />
        <Stat label="Review" value={rs["Review"] || 0} testid="stat-review" />
        <Stat label="Removal candidates" value={rs["Removal Candidate"] || 0} testid="stat-removal" />
      </div>

      <div className="mt-4">
        <ReviewChip status="Unreviewed" /> <span className="text-sm text-stone-500 ml-1">{rs["Unreviewed"] || 0} not yet reviewed</span>
      </div>

      <h2 className="text-lg font-medium text-stone-800 mt-10 mb-3">Resource counts</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {Object.entries(counts).sort().map(([t, c]) => (
          <div key={t} className="flex items-center justify-between bg-white border border-stone-200 rounded-lg px-4 py-2.5" data-testid={`count-${t}`}>
            <span className="text-sm text-stone-600">{t.replace(/_/g, " ")}</span>
            <span className="mono text-stone-900">{c}</span>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="enrichment-status">
        <div className="flex items-center gap-2 text-amber-900 font-medium">
          <Icons.ShieldCheck className="w-4 h-4" /> Internal enrichment (Smart List criteria, Lead Flow, Tags)
        </div>
        <p className="text-sm text-amber-800/80 mt-1">
          {enr.available
            ? `Enabled · Smart Lists: ${enr.smart_lists ? "captured" : "no"}, Lead Flow: ${enr.lead_flows ? "captured" : "no"}, Tags: ${enr.tags ? "captured" : "no"}`
            : "Deferred — requires a backend FUB_SESSION_COOKIE. The public-API catalog is fully functional without it."}
        </p>
      </div>
    </div>
  );
}
